import { Formik, FormikProps } from 'formik';
import React from 'react';
import { Box, Button, Link, Text, useToast, VStack } from '@chakra-ui/react';
import FormikDownshiftSelect from '@components/app/form/select/FormikDownshiftSelect';
import CountryCombobox from '@components/app/form/select/CountryCombobox';
import TextInput from '@components/app/form/TextInput';
import { isEmail } from 'validator';
import CheckboxForm from '@components/app/form/CheckboxForm';
import { unfinishedSignUp } from '@/api/user';
import mapApiErrorsToFormikErrors from '@/form/mapApiErrorsToFormikErrors';
import { Country } from '@/types/core/Country';
import { ComboboxItem } from '@components/app/form/select/downshift/types';
import NextLink from 'next/link';

interface FormValues {
  email: string;
  repeatedEmail: string;
  plainPassword: string;
  repeatedPlainPassword: string;
  acceptPolicy: boolean;
  acceptPrivacyPolicy: boolean;
  country: ComboboxItem;
  captcha: null | string;
}

function FormikForm({
  submitCount,
  isValid,
  isValidating,
  isSubmitting,
  errors,
  handleSubmit,
  values,
}: FormikProps<FormValues>) {
  return (
    <form
      onSubmit={evt => {
        evt.preventDefault();
        handleSubmit(evt);
      }}
      noValidate
    >
      <VStack spacing={{ base: '1rem', sm: '1.25rem' }} align="stretch">
        <Box>
          <FormikDownshiftSelect
            label={'Mon pays'}
            name={'country'}
            SelectComponent={CountryCombobox}
            id={'country'}
            placeholder={'Sélectionnez mon pays'}
            fields={{ country: 'name,code' }}
            orders={['name']}
            required
            requiredMessage={'Pays requis'}
          />
          <Text fontSize="xs">
            Votre pays n'apparaît pas encore dans la liste&nbsp;? Consultez la
            page{' '}
            <NextLink passHref href={'/faq'}>
              <Link
                as={'a'}
                textDecoration="underline"
                _hover={{ textDecoration: 'none' }}
              >
                FAQ
              </Link>
            </NextLink>
            .
          </Text>
        </Box>
        <Box>
          <TextInput
            name="email"
            label="Mon adresse email"
            type="email"
            autoComplete="email"
            required
          />
          <Text fontSize="xs">Cet email ne sera pas rendu public.</Text>
        </Box>
        <TextInput
          name={'repeatedEmail'}
          label={'Confirmer votre adresse email'}
          type={'email'}
          autoComplete="email"
          required
        />
        <TextInput
          name={'plainPassword'}
          label={'Mon mot de passe'}
          type={'password'}
          autoComplete="new-password"
          required
        />
        <TextInput
          name={'repeatedPlainPassword'}
          label={'Confirmer votre mot de passe'}
          type={'password'}
          autoComplete="new-password"
          required
        />
        <CheckboxForm name={'acceptPolicy'} required>
          <Text as="span">
            J'accepte les&nbsp;
            <NextLink passHref href={'/conditions-generales-d-utilisation'}>
              <Link
                as={'a'}
                isExternal
                textDecoration="underline"
                _hover={{ textDecoration: 'none' }}
              >
                CGU
              </Link>
            </NextLink>
          </Text>
        </CheckboxForm>
        <CheckboxForm name={'acceptPrivacyPolicy'} required>
          <Text as="span">
            J'ai pris connaissance de la&nbsp;
            <NextLink passHref href={'/politique-de-confidentialite'}>
              <Link
                as={'a'}
                isExternal
                textDecoration="underline"
                _hover={{ textDecoration: 'none' }}
              >
                politique de confidentialité
              </Link>
            </NextLink>
          </Text>
        </CheckboxForm>
        <TextInput
          isInline
          name={'captcha'}
          label={'Question de sécurité : Combien font 3 + 2 ?'}
          required
          htmlSize={3}
          width={'auto'}
        />
        <Box textAlign={{ base: 'center', md: 'right' }}>
          <Button
            type="submit"
            isLoading={isSubmitting}
            disabled={isSubmitting}
          >
            Continuer
          </Button>
        </Box>
      </VStack>
    </form>
  );
}

export default function UnfinishSignUpForm({
  onSubmitEnd,
}: {
  onSubmitEnd?: (userId: string, country: Country) => void;
}) {
  const toast = useToast();

  const initialValues: FormValues = {
    email: '',
    repeatedEmail: '',
    plainPassword: '',
    repeatedPlainPassword: '',
    acceptPolicy: false,
    acceptPrivacyPolicy: false,
    country: null,
    captcha: null,
  };

  return (
    <Formik
      onSubmit={async (data, actions) => {
        let response: Response;
        try {
          response = await unfinishedSignUp({
            countryId: data.country.id,
            password: data.plainPassword,
            email: data.email,
            captcha: parseInt(data.captcha as string),
          });
        } catch (e) {
          actions.setSubmitting(false);
          toast({
            title: 'Erreur',
            description: 'Une erreur est survenue',
            status: 'error',
            duration: 9000,
            isClosable: true,
          });
          return;
        }
        if (response.ok) {
          const responseData = await response.json();
          if (onSubmitEnd) {
            onSubmitEnd(responseData.data.id, data.country.item as Country);
          }

          return;
        }

        if (
          response.status === 400 &&
          response.headers.get('Content-Type') === 'json+errors'
        ) {
          const formErrors = await response.json();

          actions.setErrors(mapApiErrorsToFormikErrors(formErrors));
        }
        actions.setSubmitting(false);
      }}
      initialValues={initialValues}
      validate={data => {
        let errors: {
          email?: string[];
          repeatedEmail?: string[];
          plainPassword?: string[];
          repeatedPlainPassword?: string[];
          captcha?: string[];
        } = {};

        if (data.email !== data.repeatedEmail) {
          errors.repeatedEmail = [
            "La confirmation de l'adresse email est différente de l'adresse email",
          ];
        }

        if (data.plainPassword !== data.repeatedPlainPassword) {
          errors.repeatedPlainPassword = [
            'La confirmation du mot de passe est différente du mot de passe',
          ];
        }

        if (data.plainPassword && !data.plainPassword.match(/^.{8,}$/)) {
          errors.plainPassword = [
            'Le mot de passe doit contenir au minimum 8 caractéres',
          ];
        }

        if (data.email && !isEmail(data.email)) {
          errors.email = ["Cette valeur n'est pas une adresse email valide"];
        }

        if (data.captcha && parseInt(data.captcha) !== 5) {
          errors.captcha = ['Réponse incorrecte'];
        }

        return errors;
      }}
    >
      {formProps => {
        return <FormikForm {...formProps} />;
      }}
    </Formik>
  );
}
